import { motion } from "framer-motion";
import { FaLaptop, FaChalkboardTeacher } from "react-icons/fa";

const plans = [
  {
    icon: FaLaptop,
    title: "Online Classes",
    blurb: "Learn from anywhere with our live and recorded virtual sessions.",
    features: [
      "Live Zoom Sessions",
      "Access to Recorded Classes",
      "Digital Notes & PDFs",
      "Doubt Solving via Chat",
    ],
    featured: false,
  },
  {
    icon: FaChalkboardTeacher,
    title: "Offline Classes",
    blurb:
      "Join our campus and experience direct mentorship and hands-on learning.",
    features: [
      "In-person Classroom Sessions",
      "Printed Materials",
      "Hands-on Practice",
      "Instant Doubt Clarification",
    ],
    featured: true,
  },
];

export default function ClassTypes() {
  return (
    <section className="bg-white px-6 py-24 sm:py-28 lg:px-8">
      {/* Header */}
      <div className="mx-auto max-w-3xl text-center">
        <span className="eyebrow mb-4">Class Options</span>
        <h2 className="section-heading">Online &amp; Offline Modes</h2>
        <p className="mx-auto mt-5 max-w-2xl text-base text-ink-soft sm:text-lg">
          Choose the learning style that suits you best – flexible online
          sessions or an immersive in-person experience.
        </p>
      </div>

      {/* Cards */}
      <div className="mx-auto mt-16 grid max-w-lg grid-cols-1 gap-8 sm:mt-20 lg:max-w-5xl lg:grid-cols-2">
        {plans.map((plan, i) => {
          const Icon = plan.icon;
          return (
            <motion.div
              key={plan.title}
              whileHover={{ y: -6 }}
              whileInView={{ opacity: 1, y: 0 }}
              initial={{ opacity: 0, y: 50 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.5, delay: i * 0.15 }}
              className={`relative rounded-3xl p-8 shadow-card transition-shadow hover:shadow-brand ${
                plan.featured
                  ? "bg-ink text-white ring-1 ring-ink"
                  : "bg-white ring-1 ring-gray-200"
              }`}
            >
              {plan.featured && (
                <span className="absolute right-6 top-6 rounded-full bg-brand px-3 py-1 text-xs font-semibold uppercase tracking-wide text-white">
                  Most Popular
                </span>
              )}

              <div
                className={`flex h-14 w-14 items-center justify-center rounded-2xl text-2xl ${
                  plan.featured
                    ? "bg-brand text-white"
                    : "bg-gray-100 text-ink"
                }`}
              >
                <Icon />
              </div>

              <h3
                className={`mt-5 font-display text-xl font-bold ${
                  plan.featured ? "text-white" : "text-ink"
                }`}
              >
                {plan.title}
              </h3>
              <p
                className={`mt-2 text-sm sm:text-base ${
                  plan.featured ? "text-gray-300" : "text-ink-soft"
                }`}
              >
                {plan.blurb}
              </p>

              <ul className="mt-6 space-y-3 text-sm">
                {plan.features.map((item) => (
                  <li key={item} className="flex items-start gap-x-3">
                    <CheckIcon
                      className={plan.featured ? "text-brand-300" : "text-brand"}
                    />
                    <span
                      className={plan.featured ? "text-gray-200" : "text-ink-soft"}
                    >
                      {item}
                    </span>
                  </li>
                ))}
              </ul>
            </motion.div>
          );
        })}
      </div>
    </section>
  );
}

function CheckIcon({ className }) {
  return (
    <svg
      className={`h-6 w-5 flex-none ${className}`}
      viewBox="0 0 20 20"
      fill="currentColor"
    >
      <path
        fillRule="evenodd"
        d="M16.704 4.153a.75.75 0 0 1 .143 1.052l-8 10.5a.75.75 0 0 1-1.127.075l-4.5-4.5a.75.75 0 1 1 1.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 0 1 1.05-.143Z"
        clipRule="evenodd"
      />
    </svg>
  );
}
